import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { commercialError } from '../commercial/useCommercialApi';

/**
 * D18-C — SuperAdmin catalog governance (owner-only; rendered inside CommercialControlTower which is
 * mounted behind SuperAdmin auth). Read + draft views for the catalog dimensions the PlanVersionEditor did
 * not cover: activity rate card, credit packs, transaction fees, tenant-impact preview, and audit history.
 * All data comes from the canonical /super-admin/commercial/* backend — no client-side catalog authority,
 * no hardcoded prices/rates, no raw database IDs surfaced.
 */
const CommercialCatalogGovernance = ({ api }) => {
  const [rateCard, setRateCard] = useState(null);
  const [packs, setPacks] = useState(null);
  const [fees, setFees] = useState(null);
  const [audit, setAudit] = useState([]);
  const [impactPlan, setImpactPlan] = useState('growth');
  const [impact, setImpact] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [rc, pk, fe] = await Promise.all([
          api.get('/api/v1/super-admin/commercial/catalog/rate-card'),
          api.get('/api/v1/super-admin/commercial/catalog/credit-packs'),
          api.get('/api/v1/super-admin/commercial/catalog/transaction-fees'),
        ]);
        setRateCard(rc.data); setPacks(pk.data); setFees(fe.data);
        try { const a = await api.get('/api/v1/super-admin/commercial/audit'); setAudit(a.data?.items || a.data?.audit || []); } catch (e) { /* audit optional */ }
      } catch (e) { setErr(commercialError(e)); }
    })();
    /* eslint-disable-next-line */
  }, []);

  const loadImpact = async () => {
    try {
      const r = await api.get(`/api/v1/super-admin/commercial/plan-versions/${encodeURIComponent(impactPlan)}/tenant-impact`);
      setImpact(r.data);
    } catch (e) { setErr(commercialError(e)); }
  };

  const activities = rateCard?.published?.activities || [];

  return (
    <div className="space-y-4">
      {err && <div className="text-xs bg-red-50 border border-red-200 text-red-700 rounded p-2">{err}</div>}

      {/* Rate Card */}
      <Card className="bg-white shadow-md">
        <CardHeader className="pb-3"><CardTitle className="text-lg">Activity Rate Card (v{rateCard?.published?.version ?? '—'})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <p className="text-xs text-gray-500 mb-2">AI Voice: 3 credits per minute, billed in 20-second increments (Communication). AI Voice Resolution: separate 10-credit successful-resolution charge (AI/Operations). Both disclosed.</p>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-gray-500 border-b">
              <th className="py-1 pr-3">Activity</th><th className="pr-3">Unit</th><th className="pr-3">Wallet</th><th className="pr-3">Credits</th></tr></thead>
            <tbody>
              {activities.map((a) => (
                <tr key={a.activity_key} className="border-b last:border-0">
                  <td className="py-1 pr-3">{a.display}</td>
                  <td className="pr-3 text-gray-600">{a.unit}</td>
                  <td className="pr-3"><span className={a.bucket === 'communication' ? 'text-blue-700' : 'text-purple-700'}>{a.bucket === 'communication' ? 'Communication' : 'AI/Operations'}</span></td>
                  <td className="pr-3 tabular-nums">{a.credits}{a.advertised ? <span className="text-gray-400"> — {a.advertised}</span> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Credit Packs */}
      <Card className="bg-white shadow-md">
        <CardHeader className="pb-3"><CardTitle className="text-lg">Credit Packs</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-gray-500 border-b">
              <th className="py-1 pr-3">Pack</th><th className="pr-3">Wallet</th><th className="pr-3">Credits</th><th className="pr-3">Price</th><th className="pr-3">Expiry</th><th className="pr-3">State</th></tr></thead>
            <tbody>
              {(packs?.seed_packs || []).map((p) => (
                <tr key={p.id} className="border-b last:border-0">
                  <td className="py-1 pr-3">{p.display}</td>
                  <td className="pr-3">{p.bucket === 'communication' ? 'Communication' : 'AI/Operations'}</td>
                  <td className="pr-3 tabular-nums">{p.credits ?? '—'}</td>
                  <td className="pr-3 tabular-nums">{p.price != null ? `${p.currency} ${p.price}` : 'pricing pending'}</td>
                  <td className="pr-3">{p.expiry_months} mo</td>
                  <td className="pr-3">{p.published ? <span className="text-green-700">published</span> : <span className="text-amber-600">draft (unpublished)</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Transaction Fees */}
      <Card className="bg-white shadow-md">
        <CardHeader className="pb-3"><CardTitle className="text-lg">Transaction &amp; Marketplace Fees</CardTitle></CardHeader>
        <CardContent>
          <p className="text-xs text-gray-500">Model: {fees?.model || '—'}. Default: <span className="font-medium text-gray-700">{fees?.default || 'OFF'}</span>. No fee is charged until an owner enables and publishes a rule. Settlement is out of scope for this increment.</p>
          {(fees?.rules || []).length === 0
            ? <p className="text-xs text-gray-400 mt-2">No transaction/marketplace fee rules configured (all OFF).</p>
            : <ul className="text-sm mt-2 list-disc pl-5">{fees.rules.map((r) => <li key={r.rule_key}>{r.eligible_txn_type}: {r.fee_type} — {r.enabled ? 'enabled' : 'OFF'}</li>)}</ul>}
        </CardContent>
      </Card>

      {/* Tenant Impact Preview */}
      <Card className="bg-white shadow-md">
        <CardHeader className="pb-3"><CardTitle className="text-lg">Tenant Impact Preview</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-end gap-2">
            <label className="text-xs text-gray-600">Plan
              <select className="mt-1 block border rounded px-2 py-1 text-sm" value={impactPlan} onChange={(e) => setImpactPlan(e.target.value)}>
                {['starter', 'growth', 'professional', 'scale', 'enterprise'].map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </label>
            <Button size="sm" variant="outline" onClick={loadImpact}>Preview impact</Button>
          </div>
          {impact && (
            <div className="text-sm mt-3 grid grid-cols-2 gap-y-1">
              <span className="text-gray-500">Tenants on plan</span><span className="tabular-nums">{impact.tenants_on_plan}</span>
              <span className="text-gray-500">Grandfathered</span><span className="tabular-nums">{impact.grandfathered}</span>
              <span className="text-gray-500">Eligible for optional upgrade</span><span className="tabular-nums">{impact.eligible_for_optional_upgrade}</span>
              <span className="text-gray-500">Scheduled for migration</span><span className="tabular-nums">{impact.scheduled_for_migration}</span>
              <span className="text-gray-500">Enterprise excluded</span><span className="tabular-nums">{impact.enterprise_excluded}</span>
              {(impact.warnings || []).length > 0 && <><span className="text-amber-600">Warnings</span><span className="text-amber-600">{impact.warnings.join(', ')}</span></>}
            </div>
          )}
          <p className="text-xs text-gray-400 mt-2">Read-only aggregate — no tenant is mutated and no tenant-level detail is exposed.</p>
        </CardContent>
      </Card>

      {/* Audit History */}
      <Card className="bg-white shadow-md">
        <CardHeader className="pb-3"><CardTitle className="text-lg">Commercial Audit History</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {audit.length === 0
            ? <p className="text-xs text-gray-400">No recent commercial audit events.</p>
            : <table className="w-full text-sm"><thead><tr className="text-left text-xs text-gray-500 border-b"><th className="py-1 pr-3">When</th><th className="pr-3">Actor</th><th className="pr-3">Action</th><th className="pr-3">Target</th></tr></thead>
              <tbody>{audit.slice(0, 25).map((e, i) => {
                // Render the friendly projection the API already provides; NEVER a naked UUID. Unresolved
                // actor → "Unknown administrator"; unresolved target → a clearly-labelled technical/override
                // reference (secondary), with the raw id available only as a hover title for diagnostics.
                const actor = (e.actor_display_name && e.actor_display_name !== 'Unknown staff member')
                  ? e.actor_display_name : 'Unknown administrator';
                const action = e.action_label || String(e.action || '').replace(/[._]/g, ' ');
                const rawTarget = String(e.target_id || e.resource_id || '');
                let target = e.target_display_name;
                if (!target) {
                  if (rawTarget.startsWith('ovr_')) target = `Override reference: ${rawTarget}`;
                  else if (rawTarget) target = `Technical reference: ${rawTarget.length > 12 ? rawTarget.slice(0, 8) + '…' : rawTarget}`;
                  else target = '—';
                }
                return (
                  <tr key={i} className="border-b last:border-0"><td className="py-1 pr-3 text-gray-500">{String(e.timestamp || '').slice(0, 19).replace('T', ' ')}</td>
                    <td className="pr-3">{actor}</td><td className="pr-3">{action}</td>
                    <td className="pr-3 text-gray-600" title={rawTarget || undefined}>{target}</td></tr>
                );
              })}</tbody></table>}
          <p className="text-xs text-gray-400 mt-2">Audit shows friendly references only — no secrets, provider tokens, customer data or raw database IDs.</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default CommercialCatalogGovernance;
